import React, { useState, useCallback } from 'react';
import Select from 'react-select/async';
import { LocationOption } from '../types';
import { loadLocationOptions } from '../utils/searchLocation';

interface LocationSelectProps {
  value: LocationOption | null;
  onChange: (location: LocationOption | null) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
}

const LocationSelect: React.FC<LocationSelectProps> = ({
  value,
  onChange,
  placeholder = "Search for a location...",
  isInvalid = false,
  errorMessage
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const loadOptions = useCallback(async (inputValue: string): Promise<LocationOption[]> => {
    setIsLoading(true);
    try {
      // console.log('LocationSelect: Loading options for:', inputValue);
      const options = await loadLocationOptions(inputValue);
      // console.log('LocationSelect: Received options:', options);
      return options;
    } catch (error) {
      // console.error('LocationSelect: Error loading options:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: isInvalid ? '#dc3545' : state.isFocused ? '#007bff' : '#e1e5e9',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0,123,255,0.1)' : 'none',
      minHeight: '48px',
      fontSize: '16px',
      transition: 'border-color 0.3s ease',
      '&:hover': {
        borderColor: isInvalid ? '#dc3545' : '#80bdff'
      }
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      padding: '12px 16px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      border: '1px solid #e1e5e9',
      borderRadius: '6px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#6c757d'
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#495057'
    }),
    input: (provided: any) => ({
      ...provided,
      color: '#495057'
    })
  };

  return (
    <div>
      <Select
        value={value}
        onChange={onChange}
        loadOptions={loadOptions}
        placeholder={placeholder}
        isLoading={isLoading}
        isClearable
        isSearchable
        styles={customStyles}
        noOptionsMessage={() => "Type at least 3 characters to search"}
        loadingMessage={() => "Searching locations..."}
        filterOption={() => true} // Disable client-side filtering since we're using server-side search
        cacheOptions={false} // Disable caching to ensure fresh results
        minMenuHeight={100}
        maxMenuHeight={200}
        defaultOptions={false}
      />
      {isInvalid && errorMessage && (
        <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default LocationSelect; 